import { useRouteLoaderData } from "react-router";
import { useRootLoader } from "@/hooks/useRootLoader";
import {
  readTenantHomeScreenDefault,
  resolveTenantFeatureFlag,
  resolveTenantFeatureFlagByName,
  type TenantFlagAuth,
} from "@/utils/tenantFeatureFlag";
import type { UnizoFeatureFlagKey } from "@shared-domain/unizo/featureFlags";
import type { HomeScreenPreference } from "@shared-domain/unizo/contract";

/**
 * PRODUCT_DEV-620: テナント別フィーチャーフラグ (TenantProducts.featureFlags) の判定。
 *
 * 従来は各画面で
 *   `auth.memberships.find(...)?.products?.[0]?.featureFlags?.<key> ?? UNIZO_FEATURE_FLAGS.<key>.defaultValue ?? false`
 * を都度書いていたため、フラグを複数箇所で読む本課題を機にここへ集約する。
 *
 * PRODUCT_DEV-782: 判定の本体は純関数 `resolveTenantFeatureFlag` へ出した。
 * **ローダ（React の外）からも同じ判定を使うため。** ここはフックの皮だけ。
 *
 * URL param 方式の開発用フラグ (`useFeatureFlag`) とは別物なので混同しないこと。
 */
export function useTenantFeatureFlag(key: UnizoFeatureFlagKey): boolean {
  const { auth } = useRootLoader();
  return resolveTenantFeatureFlag(auth as TenantFlagAuth | null, key);
}

/**
 * PRODUCT_DEV-908: フラグ名（文字列）を受けて判定する関数を返す。関連リンクのように、どのフラグを
 * 見るかがデータ側で決まるとき用。レジストリに無い名前は OFF。
 */
export function useTenantFeatureFlagResolver(): (name: string) => boolean {
  const { auth } = useRootLoader();
  return (name) => resolveTenantFeatureFlagByName(auth as TenantFlagAuth | null, name);
}

/** PRODUCT_DEV-1070: 会社の初期表示ページ（テナント設定）。未設定なら null。判定は `readTenantHomeScreenDefault`。 */
export function useTenantHomeScreenDefault(): HomeScreenPreference | null {
  const { auth } = useRootLoader();
  return readTenantHomeScreenDefault(auth as TenantFlagAuth | null);
}

/**
 * PRODUCT_DEV-1043: root の外（画面だけを描くテスト）でも落とさない版。root のデータが無ければ
 * レジストリの既定値で判定する。全画面の左上 ← のように、root の外でも描かれうる共通部品で使う。
 * 画面の中では従来どおり `useTenantFeatureFlag` を使う。
 */
export function useOptionalTenantFeatureFlag(key: UnizoFeatureFlagKey): boolean {
  const data = useRouteLoaderData("root") as { auth?: TenantFlagAuth | null } | undefined;
  return resolveTenantFeatureFlag(data?.auth ?? null, key);
}
